import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../../../context';
import Footer from '../components/Footer';

const steps = [
  { icon: '🔗', title: 'Share your code', desc: 'Send your unique referral code to friends and family' },
  { icon: '📲', title: 'Friend signs up', desc: 'They create a Gamapa account using your code' },
  { icon: '🛒', title: 'Friend orders', desc: 'They place their first order on Gamapa' },
  { icon: '🎁', title: 'Both get rewarded', desc: 'You earn $5 credit, they get $5 off' },
];

export default function ReferralPage() {
  const { customerUser } = useAppContext();
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const referralCode = customerUser
    ? `GAMAPA-${(customerUser.name || 'USER').replace(/\s+/g, '').toUpperCase().slice(0, 5)}-${customerUser.id || '0000'}`
    : null;

  const referralLink = referralCode
    ? `${window.location.origin}/register?ref=${referralCode}`
    : null;

  const handleCopyCode = () => {
    if (!referralCode) return;
    navigator.clipboard.writeText(referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyLink = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  const handleShare = () => {
    if (!referralCode) return;
    if (navigator.share) {
      navigator.share({
        title: 'Join Gamapa!',
        text: `Use my code ${referralCode} and get $5 off your first order!`,
        url: referralLink,
      });
    } else {
      handleCopyLink();
    }
  };

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-orange-100 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-5 shadow-sm">
            🎁
          </div>
          <p className="text-sm font-bold text-[#f97316] uppercase tracking-wider mb-2">Refer & Earn</p>
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-3">
            Share the love, earn credits
          </h1>
          <p className="text-gray-500 text-base max-w-md mx-auto leading-relaxed">
            Invite friends to Gamapa and earn{' '}
            <span className="font-bold text-[#f97316]">$5 credit</span> for every friend who
            places their first order.
          </p>
        </div>

        {/* Referral code card */}
        {customerUser && referralCode ? (
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-3xl p-8 text-white text-center mb-8 shadow-xl shadow-orange-200">
            <p className="text-sm font-semibold opacity-80 uppercase tracking-wider mb-3">
              Your Referral Code
            </p>
            <div className="bg-white/20 rounded-2xl px-6 py-4 font-mono text-2xl font-black tracking-widest mb-6 select-all">
              {referralCode}
            </div>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={handleCopyCode}
                className="bg-white text-orange-500 font-bold px-6 py-2.5 rounded-2xl hover:bg-orange-50 transition text-sm"
              >
                {copied ? '✓ Copied!' : '📋 Copy Code'}
              </button>
              <button
                onClick={handleCopyLink}
                className="bg-white/20 border border-white/40 text-white font-bold px-6 py-2.5 rounded-2xl hover:bg-white/30 transition text-sm"
              >
                {copiedLink ? '✓ Link Copied!' : '🔗 Copy Link'}
              </button>
              <button
                onClick={handleShare}
                className="bg-white/20 border border-white/40 text-white font-bold px-6 py-2.5 rounded-2xl hover:bg-white/30 transition text-sm"
              >
                📤 Share
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-orange-50 border border-orange-100 rounded-3xl p-10 text-center mb-8">
            <p className="text-2xl mb-3">🔐</p>
            <p className="text-gray-700 font-semibold mb-1">Sign in to get your referral code</p>
            <p className="text-gray-400 text-sm mb-5">Your unique code is generated once you log in.</p>
            <Link
              to="/login"
              className="bg-[#f97316] text-white font-bold px-6 py-3 rounded-2xl hover:bg-orange-600 transition inline-block"
            >
              Login / Register
            </Link>
          </div>
        )}

        {/* Reward summary */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 text-center">
            <p className="text-4xl font-black text-[#f97316] mb-1">$5</p>
            <p className="font-semibold text-gray-800 text-sm">You earn</p>
            <p className="text-xs text-gray-400 mt-1">Per successful referral</p>
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 text-center">
            <p className="text-4xl font-black text-green-500 mb-1">$5</p>
            <p className="font-semibold text-gray-800 text-sm">Friend saves</p>
            <p className="text-xs text-gray-400 mt-1">On their first order</p>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 mb-8">
          <h2 className="font-bold text-gray-900 text-xl mb-6 text-center">How it works</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center text-2xl mx-auto mb-3 shadow-sm">
                  {step.icon}
                </div>
                <p className="font-bold text-gray-800 text-sm mb-1">{step.title}</p>
                <p className="text-xs text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Terms */}
        <div className="bg-gray-50 rounded-2xl p-5 text-xs text-gray-400 border border-gray-100">
          <p className="font-semibold text-gray-600 mb-2">Terms & Conditions</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>Credit is awarded after the referred friend completes their first order of $15 or more.</li>
            <li>Referral credit expires 90 days after issuance.</li>
            <li>Self-referrals are not permitted and will be disqualified.</li>
            <li>Credits cannot be exchanged for cash or transferred.</li>
            <li>Gamapa reserves the right to modify or cancel the referral program at any time.</li>
          </ul>
        </div>
      </div>
      <Footer />
    </>
  );
}
